import { allWordsCheck } from './repeatMatch'

/**
 * The practice session as a pure state machine (V9-w-x2): what
 * `hooks/useShadow.js` did with a handful of `useState`s, written down as
 * one state and a reducer so the transitions can be tested on their own
 * and extended (V9-w's queued/plan-B state, R13-w's five repetitions)
 * without touching the audio element, the microphone or the network.
 *
 * Nothing here touches the DOM, audio or the network: the hook owns those
 * and translates what they do into the plain actions below.
 *
 * The take's lifecycle, as the page drives it today:
 *
 *   idle ──recording/started──▶ recording ──recording/stopped──▶ reviewing
 *   reviewing | result | failed ──submit/started──▶ submitting
 *   submitting ──submit/uploaded──▶ submitting (sendPhase: 'assessing')
 *   submitting ──submit/succeeded──▶ result
 *   submitting ──submit/queued────▶ queued   (V9-w: plan B, HTTP 202)
 *   submitting ──submit/failed──▶ failed (failure classified)
 *   submitting ──submit/cancelled──▶ reviewing (the take stays)
 *   reviewing | result | failed | queued ──recording/discarded──▶ idle
 *   idle ──take/restored──▶ reviewing   (a take the outbox kept, V9-w)
 *
 * R13, five repetitions (owner decision 2026-10-13). With `repsRequired`
 * above 1 (`reps/configure`, from `me_profile.features.repeatCount`) every
 * stopped recording is checked before anything else happens:
 *
 *   recording ──recording/stopped──▶ checking
 *   checking ──rep/checked (not counted)──▶ idle            same repetition again
 *   checking ──rep/checked (counted, not the last)──▶ idle  repsDone + 1
 *   checking ──rep/checked (counted, the last)──▶ reviewing → submit as above
 *
 * Only the last repetition's recording is kept, reviewed and sent; the hook
 * throws the others away as soon as they are checked. «ضبط دوباره» on the
 * last take (reviewing, failed) redoes only that one; after a result or a
 * queue receipt it starts a new practice from the first repetition. With
 * `repsRequired` 1 — the server's default — none of this exists and the
 * session is exactly the single take above.
 *
 * `queued` sits next to `result` and `failed` as a third outcome of one
 * send: the server took the recording (202 `AttemptQueuedDto`) but has not
 * scored it — Azure was unreachable, or the admin's `planB.enabled` is on.
 * It is not a failure (there is nothing to retry: the row exists, the
 * requeue job or an admin scores it, and the learner gets the usual result
 * notification) and it is not a result (no assessment, no XP). The take is
 * no longer sendable from `queued` — sending it again would only ask after
 * the row it already made — but it can be discarded and re-recorded like
 * any other.
 *
 * `takeId` is the recording's own identity — the `clientAttemptId` every
 * upload of it carries, so a retried upload answers the row the first one
 * made rather than scoring the same audio twice. It is minted outside (the
 * hook owns the randomness) and handed in on `recording/stopped`; a
 * discard drops it with the take.
 *
 * The reference player (`playing`, `position`, `speed`) is its own track
 * alongside the phase: the learner can play the reference while idle or
 * while reviewing, and the element's own controls fire in every phase, so
 * those actions are accepted whatever the phase.
 *
 * A transition the current phase does not allow leaves the state untouched
 * (same object), which is what makes re-syncing from the microphone's own
 * state idempotent.
 */

/** Playback speeds offered by the exercise, per the proposal and mockups. */
export const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5]

/** The phases of one take, in the order the page walks them. */
export const PHASES = ['idle', 'recording', 'checking', 'reviewing', 'submitting', 'result', 'failed', 'queued']

/** Phases in which a take exists and may be sent (or sent again). */
const SENDABLE = ['reviewing', 'result', 'failed']

/** Phases in which a recording is still on the device — sendable, or already queued. */
const WITH_TAKE = [...SENDABLE, 'queued']

/**
 * Which sentence of a lesson to practice.
 *
 * Mirrors `Lesson.practiceSentence()` in the Android app
 * (domain/Models.kt): sentence index 4, or the first sentence if the
 * lesson is shorter than that. The one way in is the home card (P-home),
 * which names the first sentence the learner has not tried and asks for
 * it by index; a sentence the lesson does not have falls back to the
 * usual pick rather than an empty screen.
 */
export function practiceSentence(lesson, preferredIndex = null) {
  if (!lesson?.sentences?.length) return null
  if (Number.isInteger(preferredIndex)) {
    const asked = lesson.sentences.find((sentence) => sentence.index === preferredIndex)
    if (asked) return asked
  }
  return lesson.sentences.find((sentence) => sentence.index === 4) ?? lesson.sentences[0]
}

/**
 * Which of the app's three submission-failure screens applies
 * (docs/screens.md `failed`: offline / service / timeout — one screen,
 * three messages, not three screens). `error.response` is absent when the
 * request never reached a server at all — offline or a broken connection,
 * axios's only signal for that case. `azure_timeout` (504) is the request
 * reaching the server but Azure itself not answering in time; everything
 * else the server rejected (`audio_rejected`, `azure_unavailable`,
 * `azure_not_configured`, or an outright 5xx) is bucketed as a service
 * failure — the one thing all of those share is that retrying the exact
 * same recording might still work.
 *
 * Since V9-w every upload carries `acceptQueued`, so on a V9-b server the
 * two failures plan B covers (`azure_timeout`, `azure_unavailable`) come
 * back as a 202 and never reach here. This still classifies them: an older
 * server, or a refusal plan B deliberately leaves immediate
 * (`speech_not_recognised`, `audio_rejected`), still lands on this path.
 *
 * Reads the `ApiErrorDto` shape (`{ error, message, retryable }`) straight
 * off the error rather than through `services/api`, so this module stays
 * free of the network layer; `services/api` re-exports it for the page.
 */
export function classifyFailure(error) {
  if (!error?.response) return 'offline'
  const code = error.response?.data?.error
  if (code === 'azure_timeout') return 'timeout'
  // practice.enforceRepeats: the repetitions before this take were not enough.
  // This take can never be sent; the practice starts again (parity audit 2026-10-16, item 7).
  if (code === 'repetitions_required') return 'repetitions'
  return 'service'
}

/**
 * Is this answer to `attempts_submit` a queue receipt rather than a score?
 *
 * `AttemptQueuedDto` is the only answer carrying `status: 'queued'` —
 * `AttemptResultDto` has an `assessment` and no `status` field at all — so
 * the body alone settles it and no caller has to reach past axios for the
 * HTTP status.
 */
export function isQueuedAnswer(answer) {
  return answer?.status === 'queued'
}

/**
 * A fresh session. The settings page's default speed (P-settings) is where
 * playback starts; the pills move it from there.
 */
export function initialState(speed = 1) {
  return {
    phase: 'idle',
    speed,
    // The reference player.
    playing: false,
    position: 0,
    // The review's sending panel (P-core-3): 'uploading' until the browser
    // has sent the last byte, 'assessing' while the server answers. Only a
    // new submit resets it — the panel is not shown outside 'submitting'.
    sendPhase: 'uploading',
    // 'offline' | 'timeout' | 'service' after a failed submit. Cleared by
    // the next submit only: the page keeps the failure message up through
    // a discard and a new take, until the learner sends again.
    failure: null,
    // The take's `clientAttemptId`: minted by the hook when the recording
    // stops, carried by every upload of that same recording.
    takeId: null,
    // The `AttemptQueuedDto` of a take plan B accepted without scoring.
    queued: null,
    // R13: how many repetitions a practice takes (1 = the single take), how
    // many of them have counted so far, the speaking time of the counted ones
    // before the last (sent as `localPracticeSeconds`), and the verdict on the
    // latest one — `{ counted, ratio }`, ratio null without a recognizer.
    repsRequired: 1,
    repsDone: 0,
    localSeconds: 0,
    lastRep: null,
    // Owner rule 2026-10-14: repetitions the live text did not match to the
    // sentence, in this practice — three open the lesson's text.
    wrongReps: 0,
  }
}

/**
 * A take shorter than this is a slip of the finger, not a try (the Android
 * app's rule, 700 ms): it neither counts nor counts as wrong, and is dropped.
 */
export const MIN_TAKE_SECONDS = 0.7

/** A fresh practice of the same sentence: nothing counted yet. */
function newPractice(state) {
  return { ...state, repsDone: 0, localSeconds: 0, lastRep: null, wrongReps: 0 }
}

export function reduce(state, action) {
  switch (action.type) {
    case 'speed/set':
      return { ...state, speed: action.speed }

    case 'reference/played':
      return { ...state, playing: true }
    case 'reference/paused':
    case 'reference/ended':
      return { ...state, playing: false }
    case 'reference/position':
      return { ...state, position: action.position }
    case 'reference/rewound':
      return { ...state, position: 0 }

    case 'recording/started':
      if (state.phase !== 'idle') return state
      return { ...state, phase: 'recording' }
    case 'recording/stopped':
      if (state.phase !== 'recording') return state
      // Too short to be a try (`seconds` measured from the samples; absent for a take adopted from the outbox).
      if (typeof action.seconds === 'number' && action.seconds < MIN_TAKE_SECONDS) {
        return { ...state, phase: 'idle', takeId: null, lastRep: { counted: false, ratio: null, tooShort: true } }
      }
      if (state.repsRequired > 1) return { ...state, phase: 'checking', takeId: action.takeId ?? null }
      return { ...state, phase: 'reviewing', takeId: action.takeId ?? null }
    case 'recording/discarded': {
      if (!WITH_TAKE.includes(state.phase)) return state
      const cleared = { ...state, phase: 'idle', takeId: null, queued: null }
      if (state.repsRequired <= 1) return cleared
      // After a sent practice, a new one; before it, only the last repetition again.
      if (state.phase === 'result' || state.phase === 'queued') return newPractice(cleared)
      return { ...cleared, repsDone: state.repsRequired - 1, lastRep: null }
    }

    // R13: the verdict on a stopped repetition (`repetitionCounts`, or `speechHeard` without a recognizer).
    case 'rep/checked': {
      if (state.phase !== 'checking') return state
      // `words`: the sentence's words, each marked heard or not (the «all words» check), for the panel.
      const lastRep = { counted: action.counted === true, ratio: action.ratio ?? null, words: action.words ?? null }
      if (!lastRep.counted) return { ...state, phase: 'idle', takeId: null, lastRep, wrongReps: state.wrongReps + 1 }
      const repsDone = state.repsDone + 1
      if (repsDone >= state.repsRequired) return { ...state, phase: 'reviewing', repsDone: state.repsRequired, lastRep }
      return { ...state, phase: 'idle', takeId: null, repsDone, lastRep, localSeconds: state.localSeconds + Math.max(0, action.seconds ?? 0) }
    }
    // R13: the server's repetition count, before the practice starts. Later changes wait for the next sentence.
    case 'reps/configure': {
      const count = Math.max(1, Math.floor(Number(action.count) || 1))
      if (count === state.repsRequired || state.phase !== 'idle' || state.repsDone > 0) return state
      return { ...state, repsRequired: count }
    }
    // A take the outbox kept from an earlier visit (V9-w): the last take of a
    // practice that went through its repetitions goes straight to the review.
    // One kept from a practice without them — a single take, from before R13 or
    // while practice.repeatCount was 1 — cannot be their last: it is left out
    // and the practice starts at repetition 1 (production 2026-10-14: such a
    // take came back as the 5th, sendable at once, «localReps 4, 0 seconds»).
    case 'take/restored':
      if (state.phase !== 'idle') return state
      if (!restorable(state.repsRequired, action)) return newPractice({ ...state, takeId: null })
      return { ...state, phase: 'reviewing', takeId: action.takeId ?? null, repsDone: state.repsRequired, localSeconds: action.localSeconds ?? 0, lastRep: null }

    case 'submit/started':
      if (!SENDABLE.includes(state.phase)) return state
      return { ...state, phase: 'submitting', sendPhase: 'uploading', failure: null, queued: null }
    case 'submit/uploaded':
      if (state.phase !== 'submitting') return state
      return { ...state, sendPhase: 'assessing' }
    case 'submit/succeeded':
      if (state.phase !== 'submitting') return state
      return { ...state, phase: 'result' }
    case 'submit/queued':
      if (state.phase !== 'submitting') return state
      return { ...state, phase: 'queued', queued: action.queued ?? null }
    case 'submit/failed': {
      if (state.phase !== 'submitting') return state
      const failure = classifyFailure(action.error)
      // A take refused for its repetitions is done for: no review to send it
      // from again — the practice starts over at repetition 1.
      if (failure === 'repetitions') return newPractice({ ...state, phase: 'idle', takeId: null, queued: null, failure })
      return { ...state, phase: 'failed', failure }
    }
    case 'submit/cancelled':
      if (state.phase !== 'submitting') return state
      return { ...state, phase: 'reviewing' }

    default:
      return state
  }
}

/** The microphone is open: never play the reference into it. */
export function canPlayReference(state) {
  return state.phase !== 'recording'
}

/**
 * Sobhan (client), 2026-10-16: a repetition that has said every word stops
 * by itself — the stop tap was the one thing breaking the flow. Only where
 * the «all words» rule is the judge: repetitions on, a threshold above 0, the
 * recognizer running and the microphone recording. The verdict itself is
 * still the final one (`rep/checked`); this only ends the take.
 */
export const AUTO_STOP_TAIL_MS = 500

export function autoStopReady({ repeating, threshold, recording, liveStatus, textEn, heard }) {
  if (!repeating || !(threshold > 0) || !recording || liveStatus !== 'ready') return false
  if (!Array.isArray(heard) || heard.length === 0 || !textEn) return false
  const check = allWordsCheck(textEn, heard)
  return check.words.length > 0 && check.counted
}

/** A take is sitting in the review, sendable (again). */
export function hasTake(state) {
  return SENDABLE.includes(state.phase)
}

/** A recording is still on the device — sendable, or sent and waiting in the queue. */
export function holdsRecording(state) {
  return WITH_TAKE.includes(state.phase)
}

export function isSubmitting(state) {
  return state.phase === 'submitting'
}

/** The server has the take but no score for it yet (plan B, V9-w). */
export function isQueued(state) {
  return state.phase === 'queued'
}

/** R13: the practice takes more than one repetition. */
export function isRepeating(state) {
  return state.repsRequired > 1
}

/** R13: which repetition the learner is on, 1-based (the last one once they are all done). */
export function repNumber(state) {
  return Math.min(state.repsDone + 1, state.repsRequired)
}

/** R13: the next recording is the last repetition — the one that is reviewed and sent. */
export function isLastRep(state) {
  return state.repsRequired > 1 && state.repsDone === state.repsRequired - 1
}

/**
 * R13: whether a take the outbox kept (`{ localReps }`, as stored with it) can
 * come back as the last take of a practice of `repsRequired` repetitions —
 * only if the repetitions before it were done when it was recorded.
 */
export function restorable(repsRequired, kept) {
  return repsRequired <= 1 || (Number(kept?.localReps) || 0) >= repsRequired - 1
}

/** R13: what `attempts_submit` is told about the repetitions before the sent one. */
export function localPractice(state) {
  if (state.repsRequired <= 1) return {}
  return { localReps: state.repsRequired - 1, localPracticeSeconds: Math.round(state.localSeconds) }
}
